// For external links or opening in new tab
export const goTo = (url, newTab=true) =>{
  if(newTab) {
    window.open(url, "_blank");
  } else {
    window.location.href = url;
  }
}

// Navigation function that will be set by App component
let navigateFunction = null;

export const setNavigate = (nav) => {
  navigateFunction = nav;
};

// Route-specific colors
const routeColors = {
  '/': { body: '#704E2E', theme: '#704E2E', safeTop: '#704E2E', safeBottom: '#704E2E' },           // brown
  '/about': { body: '#D9D9D9', theme: '#D9D9D9', safeTop: '#D9D9D9', safeBottom: '#75B9BE' },     // white/blue
  '/projects': { body: '#330036', theme: '#330036', safeTop: '#330036', safeBottom: '#330036' },   // purple
  '/shaker/privacy-policy': { body: '#D9D9D9', theme: '#D9D9D9', safeTop: '#D9D9D9', safeBottom: '#D9D9D9' }, // white
};

// Get or create style tag for safe area colors (persists across React re-renders)
const getSafeAreaStyleTag = () => {
  let safeAreaStyleTag = document.getElementById('safe-area-colors-style');
  if (!safeAreaStyleTag && document.head) {
    safeAreaStyleTag = document.createElement('style');
    safeAreaStyleTag.id = 'safe-area-colors-style';
    document.head.appendChild(safeAreaStyleTag);
  }
  return safeAreaStyleTag;
};

// Internal function to apply colors (called multiple times to ensure they stick)
const applyColors = (colors) => {
  // Body and HTML backgrounds
  document.documentElement.style.backgroundColor = colors.body;
  document.body.style.backgroundColor = colors.body;
  
  // Set CSS custom properties for safe areas
  document.documentElement.style.setProperty('--safe-area-top-color', colors.safeTop);
  document.documentElement.style.setProperty('--safe-area-bottom-color', colors.safeBottom);
  
  // Update style tag with !important rules to override any CSS
  const styleTag = getSafeAreaStyleTag();
  if (styleTag) {
    styleTag.textContent = `
      #safe-area-top {
        background-color: ${colors.safeTop} !important;
        z-index: 98 !important;
        min-height: 20px !important;
      }
      #safe-area-bottom {
        background-color: ${colors.safeBottom} !important;
        z-index: 98 !important;
        min-height: 20px !important;
      }
    `;
  }
  
  // Also set inline styles as backup (in case style tag doesn't work)
  const safeTop = document.getElementById('safe-area-top');
  const safeBottom = document.getElementById('safe-area-bottom');
  
  if (safeTop) {
    safeTop.style.setProperty('background-color', colors.safeTop, 'important');
  }
  if (safeBottom) {
    safeBottom.style.setProperty('background-color', colors.safeBottom, 'important');
  }
  
  // Theme color meta tag - use aggressive update method for mobile browsers
  // Remove all existing theme-color tags first to avoid duplicates
  const existingThemeMetas = document.querySelectorAll('meta[name="theme-color"]');
  existingThemeMetas.forEach(meta => meta.remove());
  
  // Create new meta tag
  const themeMeta = document.createElement('meta');
  themeMeta.name = 'theme-color';
  themeMeta.content = colors.theme;
  // Insert at the beginning of head to ensure it's processed
  const firstChild = document.head.firstChild;
  if (firstChild) {
    document.head.insertBefore(themeMeta, firstChild);
  } else {
    document.head.appendChild(themeMeta);
  }
  
  // Also update apple-mobile-web-app-status-bar-style for iOS
  const existingAppleMetas = document.querySelectorAll('meta[name="apple-mobile-web-app-status-bar-style"]');
  existingAppleMetas.forEach(meta => meta.remove());
  
  const appleThemeMeta = document.createElement('meta');
  appleThemeMeta.name = 'apple-mobile-web-app-status-bar-style';
  appleThemeMeta.content = colors.theme === '#D9D9D9' || colors.theme === '#FFFFFF' ? 'default' : 'black-translucent';
  // Insert after theme-color
  if (themeMeta.nextSibling) {
    document.head.insertBefore(appleThemeMeta, themeMeta.nextSibling);
  } else {
    document.head.appendChild(appleThemeMeta);
  }
};

// Update all page colors (body, theme-color, safe areas)
export const updatePageColors = (path) => {
  const colors = routeColors[path] || { body: '#000000', theme: '#000000', safeTop: '#000000', safeBottom: '#000000' };
  
  // Apply immediately to avoid flash
  applyColors(colors);
  
  // Apply again after React has rendered (double RAF ensures React's render cycle is complete)
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      applyColors(colors);
      
      // Apply a few more times with delays to catch any late overrides
      setTimeout(() => applyColors(colors), 100);
      setTimeout(() => applyColors(colors), 300);
    });
  });
};

// Use this for internal SPA navigation (no page reload)
// Uses replace: true to avoid building up navigation history
export const navigateTo = (path, options = {}) => {
  // Update colors immediately before navigation
  updatePageColors(path);
  
  if (navigateFunction) {
    navigateFunction(path, { replace: true, ...options });
  } else {
    // Fallback if navigate not set
    window.location.href = path;
  }
};
